import React from "react";
import log from "js/core/logger";
import { app } from "js/namespaces";
import { CanvasEventType } from "common/constants";
import { _ } from "js/vendor";
import { SVGGroup } from "js/core/utilities/svgHelpers";
import * as geom from "js/core/utilities/geom";

const punctuationRegex = new RegExp(/[\u2000-\u200F\u201A-\u206F\u2E00-\u2E7F\\!"#$%&()*+,.:;<=>?@\[\]^_`{|}~]/g);
const singleQuoteRegex = new RegExp(/^[']+|[']+$/g);
const alternateSingleQuoteRegex = new RegExp(/[\u2018\u2019]/g);
const htmlRegex = new RegExp(/(<([^>]+)>)/ig);
const hyphenRegex = new RegExp(/[\u2010-\u2015\-/]/g);
const firebaseRegex = new RegExp(/[.#$/\[\]]/g);
const startsWithNumberRegex = new RegExp(/^\d/);
const startsWithLowercase = new RegExp(/^[a-z]/);

let capitalize = string => string.charAt(0).toUpperCase() + string.slice(1);

class Spellcheck {
    static cleanText(text) {
        return text.replace(htmlRegex, " ").replace(hyphenRegex, " ").replace(punctuationRegex, "").replace(firebaseRegex, "").replace(alternateSingleQuoteRegex, "'");
    }

    constructor(textElement) {
        this.textElement = textElement;
        this.underlines = [];
        this.flaggedTokens = [];

        this.textElement.spellcheck = this;
        this.textElement.addRenderUIFunction(this.renderSpellcheckLines);
    }

    renderSpellcheckLines = () => {
        if (this.textElement.canvas.isAnimating) {
            return null;
        }

        let misspellings = new Set(this.flaggedTokens);

        let lines = [];
        let startIndex = 0;
        this.textElement.calculatedProps.textLayout.lines.map(line => {
            line.words.map((word, i) => {
                if (misspellings.has(Spellcheck.cleanText(word.text)) || word.text.split("-").find(substring => misspellings.has(Spellcheck.cleanText(substring)))) {
                    let wordBounds = new geom.Rect(word.x, line.y, word.width, line.fontHeight).offset(this.textElement.offsetX, this.textElement.offsetY);
                    lines.push(<line
                        key={`line${lines.length}`}
                        stroke="#dd0000"
                        strokeWidth="1"
                        strokeDasharray="4px 3px"
                        x1={wordBounds.left}
                        x2={wordBounds.right}
                        y1={wordBounds.bottom + Math.max(5, line.fontHeight / 3.33)}
                        y2={wordBounds.bottom + Math.max(5, line.fontHeight / 3.33)}
                    />);
                }
            });
            startIndex += line.words.length;
        });

        return <SVGGroup key="spellcheck">{lines}</SVGGroup>;
    }

    addWord(text) {
        log.info(log.SPELLCHECK)(`Adding ${text} to user dictionary`);
        app.dictionary.update({ [Spellcheck.cleanText(text).toLowerCase()]: true });
        app.mainView.editorView.selectionLayer.checkSpelling();
    }

    isInCustomDictionary(text) {
        return app.dictionary.has(Spellcheck.cleanText(text).toLowerCase());
    }

    getSuggestions(text) {
        text = text.replace(punctuationRegex, "");
        let token, suggestions;
        if (hyphenRegex.test(text)) {
            token = this.flaggedTokens.find(token => text.indexOf(token) > -1);
            if (!token) return [];

            suggestions = app.dictionary.getSuggestions(token);

            if (token && suggestions) {
                suggestions = suggestions.map(suggestion => text.replace(token, suggestion));
            }
        } else {
            token = this.flaggedTokens.find(token => token === text);
            if (!token) return [];

            suggestions = app.dictionary.getSuggestions(token);

            if (startsWithLowercase.test(token) && suggestions.indexOf(capitalize(token)) === -1 && app.dictionary.check(capitalize(token))) {
                suggestions.unshift(capitalize(token));
            }
        }
        return suggestions;
    }

    checkSpelling(refreshRender = true) {
        if (this.skipCheck()) {
            // Removing previously flagged tokens
            if (this.flaggedTokens.length) {
                this.flaggedTokens = [];
                if (refreshRender && !this.textElement.canvas.layouter.isGenerating) {
                    this.textElement.canvas.layouter.refreshRender(false);
                }
            }

            return;
        }

        let text = Spellcheck.cleanText(this.textElement.textModel.text);

        const flaggedTokens = text.split(/\r|\s/).filter(word => {
            word = word.trim().replace(singleQuoteRegex, "");
            if (word.length < 3) return false;
            if (startsWithNumberRegex.test(word)) return false;
            return !app.dictionary.check(word);
        });

        if (_.isEqual(this.flaggedTokens, flaggedTokens)) {
            return;
        }

        this.flaggedTokens = flaggedTokens;
        if (refreshRender && !this.textElement.canvas.layouter.isGenerating) {
            this.textElement.canvas.layouter.refreshRender(false);
        }
    }

    skipCheck() {
        return !this.textElement || !this.textElement.shouldCheckSpelling || this.textElement.canvas.isPlayback || (app.user && app.user.get("ignoreSpellcheck"));
    }

    destroy() {
        this.textElement.removeRenderUIFunction(this.renderSpellcheckLines);
        this.textElement.canvas.stopListening(this.textElement.canvas, CanvasEventType.REFRESH);
        this.textElement = null;
        this.flaggedTokens = null;
    }
}

export { Spellcheck };
